<script lang="ts" setup>
import { sleep } from '@antfu/utils';
import { CATALOG_FILTER_TYPES } from '~/constants/catalogFilterTypes';
import { CATALOG_PAGE_TYPES } from '~/constants/catalogPageTypes';
import type { Pagination } from '~/types/Pagination';
import type {
  CatalogPageData,
  Filter,
  FilterOption,
  ItemListSchema,
  OfferMedium,
  RecommendationsResponse,
  SortingOption,
} from '~/types/Catalog';

interface ActiveFilter {
  filterSlug: string;
  filterTitle: string;
  valueSlug?: string;
  valueText: string;
}

const props = defineProps<{
  filters?: Filter[];
  offers: OfferMedium[];
  pagination?: Pagination;
  schema?: ItemListSchema;
  sortOptions?: SortingOption[];
}>();

const catalogStore = useCatalogStore();
const siteStore = useSiteStore();
const { catalogPageIsLoading } = storeToRefs(catalogStore);
const selectedSortOption = ref<SortingOption | null>(props.sortOptions ? props.sortOptions[0] : null);
const openFilters = ref(props.filters?.map((filter) => filter.slug));
const isShowMobileFilters = ref(false);
const route = useRoute();
const { appUrl } = useRuntimeConfig().public;
const { isMobile } = useBreakpoints();

// @ts-ignore next-line
useJsonld(props.schema ? () => ({ ...props.schema, url: `${appUrl}${route.fullPath}` }) : null);

async function changeSort(newSortOption: SortingOption) {
  selectedSortOption.value = newSortOption;

  await navigateTo({
    path: route.path,
    query: { ...route.query, sort: selectedSortOption.value?.value },
  });
}

const { sendPageViewOffersEventToDataLayer } = useGtmModule();

function sendViewedOffersToDataLayer(offers?: OfferMedium[]) {
  if (import.meta.client) sendPageViewOffersEventToDataLayer('view_item_list', offers || props.offers);
}

watch(
  () => route.query.page,
  async () => {
    await sleep(3000);
    sendViewedOffersToDataLayer();
  },
);

const fastCategoryFilters = computed<FilterOption[]>(() => {
  const categoryFilter = props.filters?.find((f) => f.slug === 'categories');

  return categoryFilter
    ? categoryFilter.options
        .filter((o) => o.fast_filter)
        .sort((a, b) => a.fast_filter - b.fast_filter)
        .filter((o) => !activeFilters.value.map((f) => f.valueSlug).includes(o.value as string))
    : [];
});

function addCategoryFilter(filter: FilterOption) {
  const query = structuredClone(route.query);

  query[`filter[categories]`] = filter.value as string;

  return navigateTo({
    path: route.path,
    query,
  });
}

const activeFilters = ref<ActiveFilter[]>([]);

watch(
  () => route.query,
  () => getActiveFiltersPillsFromQuery(),
  { immediate: true },
);

function getActiveFiltersPillsFromQuery() {
  const prevActiveFilters = JSON.parse(JSON.stringify(activeFilters.value));

  activeFilters.value = [];

  if (!props.filters) return;

  for (const [k, v] of Object.entries(route.query)) {
    const skippedQueryKeys = ['filter[search]'];

    if (skippedQueryKeys.includes(k) || !k.startsWith('filter[')) continue;

    const key = k.replace(/filter\[(.*?)]/, '$1');
    const filter = props.filters.find((f) => f.slug === key);

    if (!filter) continue;

    if (filter.title === 'БазовыйЦвет') filter.title = 'Цвет'; // костылик

    if (typeof v === 'string') {
      if (filter.type === CATALOG_FILTER_TYPES.RANGE) {
        const rangeValues = v.replace(';', ' – ');

        activeFilters.value.push({
          filterTitle: filter.title,
          filterSlug: filter.slug,
          valueText: filter.slug === 'price' ? `${rangeValues} ₽` : rangeValues,
        });
      } else if (filter.type === CATALOG_FILTER_TYPES.CHECKBOX) {
        const checkboxValues = v.split(';').map((slug) => {
          const text =
            props.filters?.find((f) => f.slug === key)?.options.find((option) => option.value === slug)?.text ??
            prevActiveFilters.find((f) => f.valueSlug === slug)?.valueText ??
            slug.toUpperCase();

          return { slug, text };
        });

        for (const value of checkboxValues) {
          activeFilters.value.push({
            filterTitle: filter.title,
            filterSlug: filter.slug,
            valueSlug: value.slug,
            valueText: value.text,
          });
        }
      } else if (filter.type === CATALOG_FILTER_TYPES.RADIO) {
        activeFilters.value.push({
          filterTitle: filter.title,
          filterSlug: filter.slug,
          valueSlug: v,
          valueText:
            props.filters?.find((f) => f.slug === key)?.options.find((option) => option.value === v)?.text || v,
        });
      } else if (filter.type === CATALOG_FILTER_TYPES.NUMERIC) {
        activeFilters.value.push({
          filterTitle: filter.title,
          filterSlug: filter.slug,
          valueSlug: v,
          valueText: v,
        });
      }
    }
  }

  activeFilters.value.sort((a, b) => (a.filterTitle > b.filterTitle ? 1 : -1));
}

async function removeFilter(filter: ActiveFilter) {
  const query = structuredClone(route.query);
  const filterSlug = `filter[${filter.filterSlug}]`;

  if (filter.valueSlug) {
    const filterValues = query[filterSlug];

    if (typeof filterValues === 'string') {
      const filteredValues = filterValues
        .split(';')
        .filter((value) => value !== filter.valueSlug)
        .join(';');

      if (filteredValues) {
        query[filterSlug] = filteredValues;
      } else {
        delete query[filterSlug];
      }
    }
  } else {
    delete query[filterSlug];
  }

  await navigateTo({
    path: route.path,
    query,
  });
}

const { sendOfferEventToDataLayer } = useGtmModule();

async function clearFilters() {
  selectedSortOption.value = props.sortOptions ? props.sortOptions[0] : null;

  if (route.query['filter[search]']) {
    await navigateTo({ path: route.path, query: { 'filter[search]': route.query['filter[search]'] } });
  } else {
    await navigateTo({ path: route.path, query: {} });
  }
}

function switchFiltersIsOpenState() {
  if (openFilters.value?.length) {
    openFilters.value = [];
  } else {
    openFilters.value = props.filters?.map((filter) => filter.slug);
  }
}

const combinedOffers = ref(props.offers);
const computedPagination = ref(props.pagination);

watch(
  () => props.offers,
  () => (combinedOffers.value = props.offers),
);
watch(
  () => props.pagination,
  () => (computedPagination.value = props.pagination),
);

const pagesCount = computed(() =>
  computedPagination.value ? Math.ceil(computedPagination.value.total / computedPagination.value.per_page) : 0,
);
const isLastPage = computed(() => computedPagination.value?.page === pagesCount.value);

async function loadMore() {
  if (!computedPagination.value || isLastPage.value) {
    return;
  }

  const page = String(computedPagination.value.page + 1);

  window.history.replaceState(
    window.history.state,
    '',
    `${route.path}?${new URLSearchParams({ ...route.query, page }).toString()}`,
  );
  route.query.page = page;

  let data;

  switch (catalogStore.currentPageType) {
    case CATALOG_PAGE_TYPES.CATEGORY_PAGE:
    case CATALOG_PAGE_TYPES.SEO_CATEGORY_PAGE:
      data = await catalogStore.getCatalogData();
      break;
    case CATALOG_PAGE_TYPES.SEARCH:
      data = await catalogStore.getSearchResults();
      break;
    case CATALOG_PAGE_TYPES.FAVORITE:
      data = await catalogStore.getFavorites();
      break;
    case CATALOG_PAGE_TYPES.BRAND:
      data = await catalogStore.getBrandBySlug();
      break;
    case CATALOG_PAGE_TYPES.SALE:
      data = await catalogStore.getSale();
      break;
    case CATALOG_PAGE_TYPES.SALE_COLLECTION:
      data = await catalogStore.getSaleCollection();
      break;
    case CATALOG_PAGE_TYPES.NEW:
      data = await catalogStore.getNew();
      break;
    default:
      break;
  }

  if (data?.data) {
    const catalogPageData = data?.data as CatalogPageData;

    computedPagination.value = catalogPageData.pagination;
    combinedOffers.value = [...combinedOffers.value, ...catalogPageData.offers];

    await nextTick();
    sendViewedOffersToDataLayer(catalogPageData.offers);

    if (window?.ym) {
      const { yandexMetrikaId } = useRuntimeConfig().public;
      window.ym(yandexMetrikaId, 'hit', window.location.href);
    }
  }
}

const productsCountPluralized = computed(() => {
  const count = computedPagination.value?.total ?? combinedOffers.value.length ?? 0;

  return `${count} ${getNoun(count, 'товар', 'товара', 'товаров')}`;
});

const recommendationsBlock = ref<RecommendationsResponse>();
const recommendedOffers = ref<OfferMedium[]>();
const isNoOffers = computed(() => props.offers.length === 0 && activeFilters.value.length === 0);

onMounted(() => {
  if (route.query.sort && props.sortOptions) {
    const sortOptionInURL = props.sortOptions.find((option) => option.value === route.query.sort);

    if (sortOptionInURL) {
      selectedSortOption.value = sortOptionInURL;
    }
  }

  if (import.meta.client && window.r46 && isNoOffers.value) {
    for (const block of siteStore.recommendationBlocks) {
      if (block.type === 'page_without_offers') {
        window.r46('recommend', block.block_identifier, {}, async function (response: RecommendationsResponse) {
          if (!response.recommends.length) return;

          recommendationsBlock.value = { ...response, block_identifier: block.block_identifier };
          recommendedOffers.value = await catalogStore.getOffersList(response.recommends.join(','));
        });
      }
    }
  }

  sendViewedOffersToDataLayer();
});
</script>

<template>
  <div class="products-listing">
    <section
      v-if="filters?.length && !isNoOffers"
      class="products-listing__filters"
    >
      <div class="flex items-center mt-2">
        {{ productsCountPluralized }}
      </div>

      <div class="flex flex-col rounded-xl p-4 gap-4 bg-white shadow-xl">
        <button @click="switchFiltersIsOpenState">
          <h3 class="flex items-center justify-start gap-2">
            Фильтры

            <SvgoChevronDown
              :class="{ 'rotate-180': openFilters?.length }"
              filled
            />
          </h3>
        </button>

        <FilterItem
          v-for="filter in filters"
          :key="filter.slug"
          :filter
          :is-open="openFilters?.includes(filter.slug)"
        />

        <TheBtn
          :is-loading="catalogPageIsLoading"
          color="secondary"
          size="md"
          @click="clearFilters"
        >
          Сбросить
        </TheBtn>
      </div>
    </section>

    <ProductCardsGallery
      v-if="isNoOffers && recommendedOffers?.length"
      :offers="recommendedOffers"
      :recommendations-block-id="recommendationsBlock?.block_identifier"
      :title="`<h2>${recommendationsBlock?.title}</h2>`"
      class="!p-0"
      no-padding
    />

    <section
      v-else
      class="products-listing__content"
    >
      <div
        v-if="fastCategoryFilters.length"
        class="flex gap-2 items-center flex-wrap"
      >
        <button
          v-for="filter in fastCategoryFilters"
          :key="filter.value"
          class="products-listing__filter-pill products-listing__filter-pill--fast-filter"
          @click="addCategoryFilter(filter)"
        >
          {{ filter.text }}
        </button>
      </div>

      <section class="flex items-center justify-between md:justify-start gap-2 flex-wrap">
        <Dropdown
          v-if="sortOptions"
          v-model="selectedSortOption"
          :options="sortOptions"
          class="order-1"
          option-label="title"
          @change="changeSort"
        />

        <section
          v-if="activeFilters.length"
          class="products-listing__active-filters"
        >
          <button
            v-for="filter in activeFilters"
            :key="filter.valueText"
            class="products-listing__filter-pill products-listing__filter-pill--active-filter"
            name="selected-filter-pill"
            title="Сбросить фильтр"
            @click="removeFilter(filter)"
          >
            {{ filter.valueText }}

            <SvgoCross
              :font-controlled="false"
              class="size-4"
              filled
            />
          </button>
        </section>

        <button
          v-if="filters?.length"
          :class="{ 'products-listing__mobile-filters-btn--active': activeFilters.length }"
          class="products-listing__mobile-filters-btn"
          @click="isShowMobileFilters = !isShowMobileFilters"
        >
          <span
            v-if="activeFilters.length"
            class="products-listing__active-filters-count"
          >
            {{ activeFilters.length }}
          </span>

          Фильтры

          <SvgoFilter filled />
        </button>

        <BaseModal
          v-if="filters?.length"
          v-model="isShowMobileFilters"
          :confirm-btn-text="`Показать (${pagination?.total})`"
          :is-loading="catalogPageIsLoading"
          cancel-btn-text="Сбросить"
          @cancel="clearFilters"
          @close="isShowMobileFilters = false"
          @confirm="isShowMobileFilters = false"
        >
          <div class="flex flex-col gap-y-2">
            <button @click="switchFiltersIsOpenState">
              <h3 class="flex items-center justify-start gap-2">
                Фильтры

                <SvgoChevronDown
                  :class="{ 'rotate-180': openFilters?.length }"
                  filled
                />
              </h3>
            </button>

            <FilterItem
              v-for="filter in filters"
              :key="filter.slug"
              :filter
              :is-open="openFilters?.includes(filter.slug)"
            />
          </div>
        </BaseModal>
      </section>

      <p
        :class="{ 'lg:hidden': filters?.length }"
        class="flex justify-start text-sm md:text-base"
      >
        {{ productsCountPluralized }}
      </p>

      <section
        v-if="combinedOffers.length"
        class="flex flex-col"
      >
        <section
          :class="{ 'products-listing__products-grid--without-filters': !filters?.length }"
          class="products-listing__products-grid"
        >
          <AppLink
            v-for="(offer, index) in combinedOffers"
            :key="offer.id"
            :to="offer.uri"
            class="hover:text-hell-black"
          >
            <ProductCard
              :id="offer.id"
              :offer
              class="!h-full"
              @click="sendOfferEventToDataLayer('select_item', offer, index)"
            />
          </AppLink>
        </section>

        <TheBtn
          v-if="!isLastPage"
          :is-loading="catalogPageIsLoading"
          :size="isMobile ? 'md' : 'lg'"
          class="mt-10"
          @click="loadMore"
        >
          Показать еще
        </TheBtn>

        <PaginationBlock
          v-if="computedPagination"
          :pagination="computedPagination"
          class="mt-6"
        />
      </section>

      <section
        v-else
        class="my-20 mx-4 text-center"
      >
        Товаров с такими фильтрами не найдено
      </section>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.products-listing {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @include screen-lg {
    flex-direction: row;
  }

  &__filters {
    display: none;

    @include screen-lg {
      display: flex;
      flex-direction: column;
      gap: 20px;
      max-width: 230px;
      height: 100%;
    }

    @include screen-xl {
      max-width: 270px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  &__products-grid {
    display: grid;
    grid-template-rows: repeat(1, minmax(0, 1fr));
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;

    @include screen-sm {
      gap: 8px;
    }

    @include screen-md {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 12px;
    }

    @include screen-xl {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 16px;
    }

    &--without-filters {
      @include screen-lg {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }

      @include screen-xl {
        grid-template-columns: repeat(5, minmax(0, 1fr));
      }
    }
  }

  &__active-filters {
    display: flex;
    flex-grow: 1;
    gap: 8px;
    align-items: center;
    order: 3;
    overflow: auto;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__active-filters-count {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background-color: $hell-green;
    color: $white;
    font-size: $text-xs;
  }

  &__filter-pill {
    display: flex;
    flex-shrink: 0;
    gap: 2px;
    align-items: center;
    border-radius: 8px;
    font-size: $text-xs;
    line-height: 1;
    transition: all 0.2s;

    &:hover {
      background-color: $hell-black-10;
      color: $hell-black-60;
    }

    &--active-filter {
      padding: 4px 6px 4px 8px;
      background-color: $hell-black-20;
    }

    &--fast-filter {
      padding: 6px 10px;
      background-color: $hell-black-10;
    }
  }

  &__mobile-filters-btn {
    display: flex;
    gap: 4px;
    align-items: center;
    order: 2;
    font-size: $text-sm;
    transition: all 0.2s;

    @include screen-md {
      order: 4;
      font-size: $text-base;
    }

    @include screen-lg {
      display: none;
    }

    &:hover {
      background-color: $hell-black-5;
      color: $hell-black-60;
    }

    &--active {
      padding: 4px 6px 4px 8px;
      border-radius: 8px;
      background-color: $hell-black-10;
    }
  }
}
</style>
